import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getApiErrorMessage } from "@/lib/api";
import {
  changePin,
  fetchConfig,
  fetchProcess,
  updateConfig,
  type ConfigView,
  type ProcessDetail,
} from "@/lib/adminSelectionApi";

export default function AdminSelectionSettings() {
  const { processId = "" } = useParams();
  const navigate = useNavigate();
  const canDefinePin = hasPermission(useApp((s) => s.account), "SELECAO.PIN.DEFINIR");
  const [process, setProcess] = useState<ProcessDetail | null>(null);
  const [config, setConfig] = useState<ConfigView | null>(null);
  const [pin, setPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([fetchProcess(processId), fetchConfig(processId)]);
      setProcess(p);
      setConfig(c);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao carregar configurações"));
    }
  }, [processId]);

  useEffect(() => { void load(); }, [load]);

  const savePin = async () => {
    if (pin.trim().length < 4) { toast.error("Informe um PIN com pelo menos 4 caracteres"); return; }
    setSavingPin(true);
    try {
      await changePin(processId, pin.trim());
      setPin(""); // never keep the PIN in memory/UI after sending
      toast.success("PIN atualizado");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao atualizar PIN"));
    } finally {
      setSavingPin(false);
    }
  };

  const toggleWriteup = async (value: boolean) => {
    try {
      const c = await updateConfig(processId, value);
      setConfig(c);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao atualizar configuração"));
    }
  };

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6">
      <button className="mb-3 text-sm text-muted-foreground underline" onClick={() => navigate("/admin/selecao")}>← Processos</button>
      <h1 className="text-xl font-semibold">Configurações</h1>
      <p className="text-sm text-muted-foreground">{process?.name}</p>

      <section className="mt-6 rounded-xl border border-border p-4">
        <h2 className="text-sm font-semibold">PIN do avaliador</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          O PIN é apenas uma separação leve entre o painel do candidato e o do avaliador. Não é um
          mecanismo de segurança forte — o acesso real continua controlado pelo login do sistema.
        </p>
        <p className="mt-2 text-xs">
          Status atual: {config == null ? "…" : config.hasPin ? "PIN definido" : "PIN ainda não definido"}
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted-foreground">Novo PIN</label>
            <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••••" />
          </div>
          {canDefinePin && <Button onClick={() => void savePin()} disabled={savingPin || pin.trim().length < 4}>Salvar PIN</Button>}
        </div>
      </section>

      <section className="mt-4 rounded-xl border border-border p-4">
        <h2 className="text-sm font-semibold">Entrevista</h2>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={config?.allowCandidateInterviewWriteup ?? false}
            onChange={(e) => void toggleWriteup(e.target.checked)} />
          Liberar registro escrito da entrevista para o candidato
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          Padrão desativado. O roteiro da entrevista pertence ao entrevistador e nunca é exposto ao candidato.
        </p>
      </section>

      <section className="mt-4 rounded-xl border border-border p-4">
        <h2 className="text-sm font-semibold">Como funciona</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          O candidato recebe um código individual de 6 caracteres para preencher a ficha. O avaliador
          acompanha as respostas pelo painel, realiza as etapas internas, atribui notas e o sistema
          calcula automaticamente a nota final e o ranking. A decisão final é sempre manual.
        </p>
      </section>
    </div>
  );
}
